import { useCallback, useMemo } from 'react';
import _ from 'lodash';

import {
  CardRewardsEarnRatesWithCard,
  CardRewardsEarnRates,
  EarnRateInfo,
} from '../../types/CardRewardsEarnRates';
import { CreditCard } from '../CreditCard';
import { CreditCardData } from '../CreditCardData';

function cardRewardsPerDollar(
  rewardsEarnRates: CardRewardsEarnRates,
  spendKey: string
) {
  let keys = [spendKey];
  const keyArray = spendKey.split('.');
  for (let i = 0; i < keyArray.length; i++) {
    // All types with subkeys have a "general" field
    const subKey = [...keyArray.slice(0, i), 'general'].join('.')
    keys.push(subKey);
  }

  return _.max(keys.map((key) => _.get(rewardsEarnRates, key) ?? 0));
}

export const useAllSpendKeys = (monthlySpend: CardRewardsEarnRates) => {
  const getPopulatedKeys = useCallback((monthlySpend: Record<string, any>, prefix = '') =>
    Object.entries(monthlySpend).flatMap(
      ([category, earnRate]: [
        category: string,
        earnRate: number | Record<string, any>
      ]): string[] => {
        const categoryKey = prefix ? `${prefix}.${category}` : category;

        if (typeof earnRate === 'object') {
          // Recursively handle nested categories
          return getPopulatedKeys(earnRate, categoryKey);
        } else {
          // Return a single element for non-nested categories
          return [categoryKey];
        }
      }
    ), []);

  // Get all the keys of monthlySpend in the form of "general", or "travel.general", or "travel.hotel.hilton"
  const allSpendKeys = useMemo(() => getPopulatedKeys(monthlySpend).sort(
    // Ensure `general` of each category is processed first
    (a: string, b: string) => {
      if (a.endsWith('general') && b.endsWith('general')) {
        return a.split('.').length - b.split('.').length;
      }
      if (a.endsWith('general')) {
        return 1;
      }
      if (b.endsWith('general')) {
        return 1;
      }

      return a.split('.').length - b.split('.').length;
    }
  ), [monthlySpend, getPopulatedKeys]);

  return allSpendKeys;
}

export function useBestEarnRates(
  cards: CreditCard[],
  monthlySpend: CardRewardsEarnRates,
  pointValues: Record<string, number>,
  enableForeignTransactionFees: boolean,
): CardRewardsEarnRatesWithCard {
  const allSpendKeys = useAllSpendKeys(monthlySpend);

  const result: CardRewardsEarnRatesWithCard = useMemo(() => allSpendKeys.reduce(
    (acc, spendKey) => {
      let bestEarnRateCardInfo: EarnRateInfo[] = [];

      for (let i = 0; i < cards.length; i++) {
        const cardId = cards[i];
        const card = CreditCardData[cardId];

        const earnRate = cardRewardsPerDollar(card.rewards_earn_rates, spendKey);
        if (!earnRate) {
          continue;
        }
        const pointValue = pointValues[card.rewardsType];
        const totalValue = pointValue * earnRate;
        const spend = _.get(monthlySpend, spendKey);

        // If no best earn rate found yet or the earn rate is better than the best earn rate
        if (
          !bestEarnRateCardInfo.length
        ) {
          bestEarnRateCardInfo = [{
            earnRate,
            card: cardId,
            rewardsType: card.rewardsType,
            totalValue,
            spend,
          }];
        } else {
          bestEarnRateCardInfo.push({
            earnRate,
            card: cardId,
            rewardsType: card.rewardsType,
            totalValue,
            spend,
          });
        }
      }

      _.set(acc, spendKey, bestEarnRateCardInfo);
      return acc;
    },
    {} as CardRewardsEarnRatesWithCard
  ), [allSpendKeys, cards, monthlySpend, pointValues]);

  // Starting from the bottom up
  // TODO: eventualy we might want to let users see what's in there
  const allSpendKeysFromLeavesUp = allSpendKeys.reverse()

  // Now, sort all earn rates by value
  allSpendKeysFromLeavesUp.forEach((spendKey) => {
    const maybeBestEarnRateCardInfoArray = _.get(result, spendKey);
    if (!maybeBestEarnRateCardInfoArray.length) {
      return;
    }
    // Sort the earn rates by value
    const sortedEarnRates = maybeBestEarnRateCardInfoArray.sort(
      (a: EarnRateInfo, b: EarnRateInfo) => b.totalValue - a.totalValue
    );
    _.set(result, spendKey, sortedEarnRates);
  });

  allSpendKeysFromLeavesUp.forEach((spendKey) => {
    const bestEarnRateCardInfo = _.get(result, spendKey)?.[0];
    if (!bestEarnRateCardInfo) {
      return;
    }
    // Now we have the best card for the category
    // Since we address the "general" before the rest of the content:

    // if the card in the best earn rate info matches the general category,
    // just group it with the general category to make things easier on the user.
    const generalKey = getGeneralKey(spendKey)
    const maybeGeneralCategory = generalKey
      ? _.get(result, generalKey)
      : undefined;
    if (
      generalKey &&
      maybeGeneralCategory &&
      bestEarnRateCardInfo &&
      // Card and multiplier must match to work
      bestEarnRateCardInfo.card === maybeGeneralCategory.card &&
      bestEarnRateCardInfo.earnRate === maybeGeneralCategory.earnRate
    ) {
      _.set(result, generalKey, [{
        ...maybeGeneralCategory,
        totalValue:
          maybeGeneralCategory.totalValue + bestEarnRateCardInfo.totalValue,
        spend: maybeGeneralCategory.spend + bestEarnRateCardInfo.spend,
      } satisfies EarnRateInfo]);

      _.set(result, spendKey, []); // also clear the old earn rate to not duplicate tracking
    }
  });

  return result;
}

/*
 * Gets the associated general key for the spend key
 * For "travel.airline.united": returns "travel.airline.general"
 * For "travel.airline.general": returns "travel.general"
 * For "streaming": returns "general"
 */
function getGeneralKey(spendKey: string) {
  // todo: add an invariant of length 1 or more
  if (!spendKey.length || spendKey === 'general') {
    return undefined
  }

  if (spendKey.endsWith('general')) {
    // console.log('generalkey for ' + spendKey + ' is ' + spendKey.split('.').slice(0, -2).concat(['general']).join('.'));
    return spendKey.split('.').slice(0, -2).concat(['general']).join('.');
  }
  // console.log('generalkey for ' + spendKey + ' is ' + spendKey.split('.').slice(0, -1).concat(['general']).join('.'))
  return spendKey.split('.').slice(0, -1).concat(['general']).join('.')
}
